/* Native Shopify section search; typing never changes keyboard focus or product search. */
(() => {
  'use strict';
  function init(root) {
    if (root.dataset.bkSearchReady) return;
    const form = root.querySelector('[data-bk-live-form]');
    const input = form?.querySelector('[name="q"]');
    const results = root.querySelector('[data-bk-live-results]');
    if (!form || !input || !results) return;
    root.dataset.bkSearchReady = 'true';
    const status = root.querySelector('[data-bk-search-status]');
    const clear = root.querySelector('[data-bk-search-clear]');
    const hint = root.querySelector('[data-bk-search-hint]');
    const original = results.innerHTML;
    const originalQuery = input.value;
    const topicLinks = [...root.querySelectorAll('.bk-blog-topics a')];
    const selected = topicLinks.find(link => link.hasAttribute('aria-current'));
    let timer, controller, sequence = 0, composing = false, failed = false;
    hint.hidden = false;
    clear.hidden = !input.value;
    function cancel() {
      clearTimeout(timer);
      controller?.abort();
      sequence += 1;
    }
    function markTopics(searching) {
      topicLinks.forEach(link => link.removeAttribute('aria-current'));
      const link = searching ? topicLinks[0] : selected;
      link?.setAttribute('aria-current', 'page');
    }
    function restore() {
      cancel();
      failed = false;
      results.innerHTML = original;
      results.removeAttribute('aria-busy');
      status.textContent = '';
      markTopics(false);
      clear.hidden = true;
      if (originalQuery) {
        const message = document.createElement('p');
        message.className = 'bk-search-empty';
        message.textContent = hint.textContent;
        results.replaceChildren(message);
      }
    }
    function urlFor(query, page) {
      const url = new URL(form.action, location.href);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'article');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('section_id', 'bk-article-results');
      if (page) url.searchParams.set('page', page);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url;
    }
    async function load(query, page = null, focusResults = false) {
      cancel();
      const request = sequence;
      controller = new AbortController();
      const activeController = controller;
      const timeout = setTimeout(() => activeController.abort(), 10000);
      status.textContent = root.dataset.loading;
      results.setAttribute('aria-busy', 'true');
      clear.hidden = !input.value;
      markTopics(Boolean(query));
      try {
        const response = await fetch(urlFor(query, page), {signal:activeController.signal,credentials:'same-origin',headers:{'X-Requested-With':'XMLHttpRequest'}});
        if (!response.ok) throw new Error('Search request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = html.querySelector('[data-bk-search-response]');
        if (!content) throw new Error('Missing search section');
        if (request !== sequence) return;
        results.replaceChildren(content);
        failed = false;
        status.textContent = `${root.dataset.resultLabel}: ${content.dataset.count || '0'}`;
        if (focusResults) {
          results.tabIndex = -1;
          results.focus({preventScroll:true});
          results.scrollIntoView({block:'start',behavior:'instant'});
        }
      } catch (error) {
        if (request !== sequence) return;
        failed = true;
        // Never leave old results silently presented as matches for a new phrase.
        results.replaceChildren();
        const message = document.createElement('p');
        message.className = 'bk-search-empty';
        message.textContent = root.dataset.error;
        results.append(message);
        status.textContent = root.dataset.error;
      } finally {
        clearTimeout(timeout);
        if (request === sequence) results.removeAttribute('aria-busy');
      }
    }
    function onInput() {
      cancel();
      if (composing) return;
      const query = input.value.trim();
      clear.hidden = !input.value;
      if (!query) { restore(); return; }
      // Mark old results stale immediately, even before the debounce request.
      results.setAttribute('aria-busy', 'true');
      status.textContent = root.dataset.loading;
      timer = setTimeout(() => load(query), 250);
    }
    input.addEventListener('input', onInput);
    input.addEventListener('compositionstart', () => { composing = true; cancel(); });
    input.addEventListener('compositionend', () => { composing = false; onInput(); });
    form.addEventListener('submit', event => {
      if (failed) return; // A real search navigation remains available after an error.
      event.preventDefault();
      const query = input.value.trim();
      if (query) load(query);
      else restore();
    });
    clear.addEventListener('click', () => { input.value = ''; restore(); input.focus(); });
    results.addEventListener('click', event => {
      const link = event.target.closest('.bk-result-pagination a');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const url = new URL(link.href);
      if (url.origin !== location.origin) return;
      event.preventDefault();
      load(input.value.trim(), url.searchParams.get('page'), true);
    });
    // Normal topic links deliberately leave search mode and retain Shopify tag pagination.
    topicLinks.forEach(link => link.addEventListener('click', cancel));
    window.addEventListener('pagehide', cancel, {once:true});
  }
  const start = () => document.querySelectorAll('[data-bk-live-search]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start,{once:true}); else start();
  document.addEventListener('shopify:section:load',start);
})();
